/* eslint-disable react-refresh/only-export-components */
import { useAuth0 } from '@auth0/auth0-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ErrorReporter from 'src/monitoring/errorReporter';
import { adminUsersQueryKeys } from 'src/modules/shared/hooks/api/admin/users';
import type {
  AuthBootstrapContextType,
  AuthBootstrapProviderProps,
} from '../types/auth/auth-bootstrap.type';
import type { AuthBootstrapError } from '../errors/auth/auth-bootstrap.error';
import {
  bootstrapCurrentUserWithRetries,
  canContinueWithExistingPermissions,
  decodePermissions,
  fetchTokenWithRetries,
  getInteractiveRecoveryReason,
  normalizeBootstrapError,
  resolvePermissionsAfterBootstrap,
  shouldAttemptInteractiveRecovery,
} from '../utils/auth/auth-bootstrap.util';
import {
  clearAuthBootstrapRecoveryAttempts,
  getAuthRedirectAppState,
  markAuthBootstrapRecoveryAttempted,
} from '../utils/auth/auth-recovery.util';
import type { AuthBootstrapRecoveryReason } from '../types/auth/auth-recovery.type';

const AuthBootstrapContext = createContext<
  AuthBootstrapContextType | undefined
>(undefined);

const EMPTY_BOOTSTRAP_STATE: AuthBootstrapContextType = {
  canRetry: false,
  error: null,
  loading: false,
  permissions: [],
  ready: false,
  retry: () => undefined,
};

/**
 * Coordinates the authenticated app bootstrap before permission-gated UI mounts.
 */
export const AuthBootstrapProvider = ({
  children,
}: AuthBootstrapProviderProps) => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
  } = useAuth0();
  const queryClient = useQueryClient();
  const [retryVersion, setRetryVersion] = useState(0);
  const [state, setState] = useState<AuthBootstrapContextType>(
    EMPTY_BOOTSTRAP_STATE
  );

  const retry = useCallback(() => {
    setRetryVersion((currentValue) => currentValue + 1);
  }, []);

  const finishWithError = useCallback(
    (error: AuthBootstrapError, active: boolean) => {
      ErrorReporter.sendException(error);

      if (!active) {
        return;
      }

      queryClient.removeQueries({ queryKey: adminUsersQueryKeys.current() });
      setState({
        error,
        loading: false,
        permissions: [],
        ready: false,
        canRetry: error.canRetry,
        retry,
      });
    },
    [queryClient, retry]
  );

  const attemptInteractiveRecovery = useCallback(
    async (active: boolean, reason: AuthBootstrapRecoveryReason) => {
      if (!active) {
        return;
      }

      try {
        markAuthBootstrapRecoveryAttempted(reason);
        await loginWithRedirect({
          appState: getAuthRedirectAppState(),
        });
      } catch (error) {
        finishWithError(normalizeBootstrapError(error, 'token'), active);
      }
    },
    [finishWithError, loginWithRedirect]
  );

  useEffect(() => {
    if (auth0Loading) {
      return;
    }

    if (!isAuthenticated) {
      clearAuthBootstrapRecoveryAttempts();
      queryClient.removeQueries({ queryKey: adminUsersQueryKeys.current() });
      setState(EMPTY_BOOTSTRAP_STATE);
      return;
    }

    let active = true;

    const bootstrap = async () => {
      setState({
        canRetry: false,
        error: null,
        loading: true,
        permissions: [],
        ready: false,
        retry,
      });

      try {
        const initialToken = await fetchTokenWithRetries(
          getAccessTokenSilently,
          'token'
        );
        let resolvedPermissions = decodePermissions(initialToken);

        if (resolvedPermissions.length === 0) {
          await bootstrapCurrentUserWithRetries(
            queryClient,
            getAccessTokenSilently
          );
          resolvedPermissions = await resolvePermissionsAfterBootstrap(
            getAccessTokenSilently
          );
        } else {
          try {
            await bootstrapCurrentUserWithRetries(
              queryClient,
              getAccessTokenSilently
            );
          } catch (error) {
            const normalizedCurrentUserError = normalizeBootstrapError(
              error,
              'current-user'
            );

            if (
              !canContinueWithExistingPermissions(
                normalizedCurrentUserError,
                resolvedPermissions
              )
            ) {
              throw normalizedCurrentUserError;
            }

            ErrorReporter.sendException(normalizedCurrentUserError);
          }
        }

        if (!active) {
          return;
        }

        setState({
          canRetry: false,
          error: null,
          loading: false,
          permissions: resolvedPermissions,
          ready: true,
          retry,
        });
        clearAuthBootstrapRecoveryAttempts();
      } catch (error) {
        const normalizedError = normalizeBootstrapError(error);
        const recoveryReason = getInteractiveRecoveryReason(normalizedError);

        if (
          recoveryReason &&
          shouldAttemptInteractiveRecovery(normalizedError)
        ) {
          await attemptInteractiveRecovery(active, recoveryReason);
          return;
        }

        finishWithError(normalizedError, active);
      }
    };

    void bootstrap();

    return () => {
      active = false;
    };
  }, [
    auth0Loading,
    attemptInteractiveRecovery,
    finishWithError,
    getAccessTokenSilently,
    isAuthenticated,
    queryClient,
    retry,
    retryVersion,
  ]);

  const value = useMemo(() => state, [state]);

  return (
    <AuthBootstrapContext.Provider value={value}>
      {children}
    </AuthBootstrapContext.Provider>
  );
};

/**
 * Returns the current auth bootstrap state for permission-gated consumers.
 *
 * @returns Current auth bootstrap state.
 */
export const useAuthBootstrap = (): AuthBootstrapContextType => {
  const context = useContext(AuthBootstrapContext);

  if (!context) {
    throw new Error(
      'useAuthBootstrap must be used within an AuthBootstrapProvider'
    );
  }

  return context;
};
